import axios from "axios";
import { msalInstance } from "src/lib/msal";
import zod from "zod";

const psTabletManagementApiInstance = axios.create({
    baseURL:
        import.meta.env.VITE_DEPLOYMENT === "DEV"
            ? "https://apim-dev-mvtecenter-w2.azure-api.net/api"
            : "",
});

psTabletManagementApiInstance.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
            const { accessToken } = await msalInstance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
            });

            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

const checkInTabletInputSchema = zod.object({
    body: zod.object({
        driverCode: zod.string(),
        driverSignatureUrl: zod.string(),
        emailReceipt: zod.string().nullable(),
        employeeCode: zod.string(),
        stylusMissing: zod.boolean(),
        tabletDamaged: zod.boolean(),
        terminal: zod.string(),
    }),
    params: zod.object({
        tabletId: zod.string(),
    }),
});

const checkInTabletResponseSchema = zod.object({
    status: zod.union([
        zod.literal("invalid_args"),
        zod.literal("ok"),
        zod.literal("error"),
    ]),
    message: zod.string(),
});

type CheckInTabletInput = zod.infer<typeof checkInTabletInputSchema>;

async function checkInTablet(input: CheckInTabletInput) {
    const response = await psTabletManagementApiInstance.post<{
        Status: "invalid_args" | "ok" | "error";
        Message: string;
    }>(
        `/PlatSciTabletMgt/pfs_tablets/${input.params.tabletId}/check-in`,
        input.body,
    );

    const validatedResponse = checkInTabletResponseSchema.parse(response.data);

    if (
        validatedResponse.status === "invalid_args" ||
        validatedResponse.status === "error"
    ) {
        throw new Error(response.data.Message);
    }
}

const checkOutTabletInputSchema = zod.object({
    body: zod.object({
        driverCode: zod.string(),
        driverSignatureUrl: zod.string(),
        employeeCode: zod.string(),
        emailReceipt: zod.string().nullable(),
        overrideReason: zod.string().optional(),
        stylusMissing: zod.boolean(),
        tabletDamaged: zod.boolean(),
        terminal: zod.string(),
        unitCode: zod.string(),
    }),
    params: zod.object({
        tabletId: zod.string(),
    }),
});

const checkOutTabletResponseSchema = zod.object({
    status: zod.union([
        zod.literal("invalid_args"),
        zod.literal("outstanding_assignment"),
        zod.literal("ok"),
    ]),
    message: zod.string(),
});

type CheckOutTabletInput = zod.infer<typeof checkOutTabletInputSchema>;
type CheckOutTabletResponse = zod.infer<typeof checkOutTabletResponseSchema>;

async function checkOutTablet(input: CheckOutTabletInput) {
    console.log(input);
    checkOutTabletInputSchema.parse(input);

    const response =
        await psTabletManagementApiInstance.post<CheckOutTabletResponse>(
            `/PlatSciTabletMgt/pfs_tablets/${input.params.tabletId}/check-out`,
            input.body,
        );

    const validatedResponse = checkOutTabletResponseSchema.parse(response.data);

    if (validatedResponse.status === "invalid_args") {
        throw new Error(validatedResponse.message);
    }

    return validatedResponse;
}

export const psTabletManagementApi = {
    checkInTablet,
    checkOutTablet,
};
